import { policy } from '@linecorp/dosi-svc-cookie';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useModal } from 'yoso';
import { MpAgreeBottomSheet } from '~/components/Dosi/Brand/Like/BottomSheet/MpAgreeBottomSheet';
import { BaseError } from '~/components/Error/BaseError';
import { T, t } from '~/components/I18n';
import { Alert } from '~/components/Modal/Alert/Alert';
import { useBottomSheet } from '~/components/Modal/BottomSheet';
import { UseDosiminMutationOptions, UseDosiminQueryOptions } from '~/components/Query/Query.hooks';
import { useAgreeTerm, useTerm } from '~/components/Term';
import { regularFormat, sharedDateTimer } from '~/components/Util';

export const brandLikeUrl = '/api/citizen/v1/like';

export type BrandLike = {
  likePressed: boolean;
};

export type LikeBrandResponse = {
  isMpAgreed?: boolean;
} & BrandLike;

export const getBrandLike = async () => {
  const { data } = await axios.get<BrandLike>(brandLikeUrl);
  return data;
};
export const useBrandLike = (options?: UseDosiminQueryOptions<BrandLike>) =>
  useQuery<BrandLike>({
    queryKey: ['brand', 'like'],
    queryFn: getBrandLike,
    initialData: { likePressed: false },
    ...options,
  });

export const dislikeBrand = async () => {
  const { data } = await axios.delete<BrandLike>(brandLikeUrl);
  return data;
};

export const useDislikeBrand = (options?: UseDosiminMutationOptions<BrandLike>) =>
  useMutation<BrandLike>({
    mutationFn: dislikeBrand,
    ...options,
  });

export const likeBrand = async () => {
  const { data } = await axios.put<LikeBrandResponse>(brandLikeUrl);
  return data;
};

export const useLikeBrand = (options?: UseDosiminMutationOptions<LikeBrandResponse>) =>
  useMutation<LikeBrandResponse>({
    mutationFn: likeBrand,
    ...options,
  });

const useProceedAgreeMpErrorHandler = () =>
  useCallback((error: BaseError) => {
    switch (error.message) {
      case 'DISMISSED':
        break;
      default:
        toast(t('citizen_error_500_toast'));
        break;
    }
  }, []);
export const useProceedAgreeMp = (options?: UseDosiminMutationOptions<void, BaseError, void>) => {
  const bottomSheet = useBottomSheet();
  const modal = useModal();
  const onError = useProceedAgreeMpErrorHandler();

  const { mutateAsync: agreeTerm } = useAgreeTerm({ onError: undefined });
  const { data: term } = useTerm('MP:MP', { useErrorBoundary: false });
  const proceededWithinWeek = !policy.shouldShow();
  const proceeded = proceededWithinWeek || term?.agree;

  return useMutation<void, BaseError, void>({
    mutationFn: async () => {
      if (proceeded) return;

      policy.set();
      await bottomSheet.open(<MpAgreeBottomSheet />).then(({ confirmed }) => {
        if (!confirmed) throw new BaseError('DISMISSED');
      });
      await agreeTerm('MP:MP');

      await modal.open(
        <Alert
          title={<T name="dosi_mp_agree_done_popup_title" />}
          description={
            <T
              name="dosi_mp_agree_done_popup_desc"
              options={{ date: regularFormat(sharedDateTimer.now) }}
            />
          }
          confirmText={t('dosi_common_ok_btn')}
          // data-event="impression"
          // data-event-category="mp_consent_complete_popup"
          // data-event-impression_id="{category}"
        />
      );
    },
    onError,
    ...options,
  });
};

const useProceedLikeBrandErrorHandler = () =>
  useCallback(() => {
    toast(t('citizen_error_500_toast'));
  }, []);

export const useProceedLikeBrand = (
  options?: UseDosiminMutationOptions<LikeBrandResponse, BaseError, void>
) => {
  const onError = useProceedLikeBrandErrorHandler();
  const {
    data: { likePressed },
  } = useBrandLike({ useErrorBoundary: false });
  const { mutateAsync: like } = useLikeBrand({ onError: undefined });
  const { mutateAsync: dislike } = useDislikeBrand({ onError: undefined });
  return useMutation<LikeBrandResponse, BaseError, void>({
    mutationFn: async () => {
      const data = likePressed ? await dislike() : await like();
      return data;
    },
    onError,
    ...options,
  });
};
